import type { ReactNode } from "react";
import { Button, Callout, Logo } from "@winche/ui";
import { ErrorBoundary } from "./error-boundary";

/*
 * Error boundaries for the admin (ux-admin-10). A section that throws while
 * rendering (a `_admin` doc with another shape) used to unmount the whole SPA,
 * and since the section lives in the hash, reloading crashed again. Now the
 * section shows what failed with a way back to Overview, and the app-level
 * boundary in main.tsx catches anything above the sections.
 */

/**
 * One section of the admin. Give it `key={section}` so moving to another
 * section starts with a fresh boundary.
 */
export function SectionErrorBoundary({
  section,
  onGoToOverview,
  children,
}: {
  section: string;
  onGoToOverview: () => void;
  children: ReactNode;
}) {
  return (
    <ErrorBoundary
      label={`[admin] ${section} section`}
      fallback={(error) => (
        <Callout
          tone="danger"
          live="assertive"
          title="This section failed to render"
          action={
            section === "overview" ? (
              <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                Reload
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={onGoToOverview}>
                Go to Overview
              </Button>
            )
          }
        >
          <span className="font-mono">{error.message || error.name}</span>. A document it reads probably has another shape; the
          browser console has the details. The rest of the admin still works.
        </Callout>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/** Last resort around the whole app: a message and Reload instead of a blank page. */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      label="[admin] app"
      fallback={(error) => (
        <div className="flex min-h-screen items-center justify-center bg-sunken p-4">
          <div className="w-full max-w-[420px] rounded-xl border border-border bg-card p-6 text-center shadow-sm">
            <Logo variant="mark" size={40} className="mx-auto mb-4 justify-center" label="" />
            <h1 className="text-title font-semibold">The admin failed to load</h1>
            <p className="mt-1 text-label text-muted-foreground">
              <span className="font-mono">{error.message || error.name}</span>
            </p>
            <Button
              className="mt-5"
              fullWidth
              variant="outline"
              onClick={() => {
                // Back to Overview: a section restored from the hash may be what crashed.
                window.location.replace(`${window.location.pathname}${window.location.search}#overview`);
                window.location.reload();
              }}
            >
              Reload Overview
            </Button>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
